import { useState } from 'react';
import ArticleLink from '@/components/feature/ArticleLink';
import Sparkline from '@/components/charts/Sparkline';
import { getTrendScenarios } from '@/utils/forexRates';

const scenarios = getTrendScenarios();

const formatRateDate = (value: string): string => {
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('th-TH', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const toneTextClass = (tone: string): string => {
  if (tone === 'down') return 'text-accent-700';
  if (tone === 'flat') return 'text-primary-700';
  return 'text-secondary-700';
};

const toneBadgeClass = (tone: string): string => {
  if (tone === 'down') return 'bg-accent-100 text-accent-800';
  if (tone === 'flat') return 'bg-primary-100 text-primary-700';
  return 'bg-secondary-100 text-secondary-900';
};

export default function Hero() {
  const [activeId, setActiveId] = useState(scenarios[0]?.id ?? 'uptrend');
  const scenario = scenarios.find((item) => item.id === activeId) ?? scenarios[0];

  return (
    <section className="relative overflow-hidden bg-background-50">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary-100/70 blur-3xl" />
      <div className="pointer-events-none absolute -left-20 top-40 h-56 w-56 rounded-full bg-accent-100/60 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:px-10 lg:py-24">
        <div className="max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3.5 py-1.5 text-xs font-medium text-primary-700">
            <i className="ri-graduation-cap-line text-base" />
            แพลตฟอร์มเรียน Forex ภาษาไทยสำหรับมือใหม่
          </span>
          <h1 className="mt-6 text-balance font-heading text-3xl font-semibold leading-tight text-foreground-950 sm:text-4xl lg:text-[52px] lg:leading-[1.1]">
            เรียนรู้ Forex ตั้งแต่พื้นฐาน
            <br />
            จนเข้าใจวิธีเทรดด้วยตัวเอง
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-foreground-600 md:text-lg">
            คู่มือ Forex สำหรับมือใหม่ เรียนรู้ตั้งแต่ Forex คืออะไร การอ่านกราฟ การเปิดออเดอร์ MT4 / MT5
            ไปจนถึงการบริหารความเสี่ยง
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ArticleLink
              to="/what-is-forex"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              เริ่มเรียนรู้ Forex
              <i className="ri-arrow-right-line text-base" />
            </ArticleLink>
            <ArticleLink
              to="/forex-basics"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-list-check text-base" />
              ดูบทความทั้งหมด
            </ArticleLink>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3">
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-book-2-line text-sm" />
              </span>
              บทความภาษาไทย
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className="ri-shield-check-line text-sm" />
              </span>
              เน้นบริหารความเสี่ยง
            </div>
            <div className="flex items-center gap-2 text-sm text-foreground-600">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                <i className="ri-smartphone-line text-sm" />
              </span>
              เรียนบนมือถือได้
            </div>
          </div>
        </div>

        <div className="w-full animate-fade-in">
          <div className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-accent-500" />
                <span className="h-2.5 w-2.5 rounded-full bg-secondary-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-primary-300" />
              </div>
              <span className="flex items-center gap-1.5 text-xs font-medium text-foreground-500">
                <i className="ri-book-open-line text-sm text-primary-600" />
                ตัวอย่างราคาประกอบบทความ
              </span>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-1 rounded-full bg-background-100 p-1">
              {scenarios.map((item) => {
                const isActive = item.id === scenario.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setActiveId(item.id)}
                    className={`flex cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-full px-2 py-1.5 text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-background-50 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-800'
                    }`}
                  >
                    <i
                      className={`text-sm ${
                        item.tone === 'down'
                          ? 'ri-arrow-down-line text-accent-600'
                          : item.tone === 'flat'
                            ? 'ri-arrow-left-right-line text-primary-600'
                            : 'ri-arrow-up-line text-secondary-600'
                      }`}
                    />
                    {item.tab}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 rounded-lg border border-background-200 bg-background-100 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-num text-xl font-semibold text-foreground-950">{scenario.price}</p>
                  <p className="mt-0.5 truncate text-xs text-foreground-500">
                    {scenario.symbol} · {scenario.name}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${toneBadgeClass(scenario.tone)}`}
                >
                  {scenario.change}
                </span>
              </div>
              <div className="mt-2 h-20 w-full">
                <Sparkline
                  key={scenario.id}
                  data={scenario.series}
                  labels={scenario.dates.map(formatRateDate)}
                  tone={scenario.tone}
                  formatValue={(value) => value.toFixed(scenario.digits)}
                />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 border-t border-background-200 pt-3">
                <div>
                  <p className="text-[11px] text-foreground-500">Spread</p>
                  <p className="font-num text-sm font-semibold text-foreground-900">{scenario.spread} pips</p>
                </div>
                <div>
                  <p className="text-[11px] text-foreground-500">ช่วงเคลื่อนไหว</p>
                  <p className="font-num text-sm font-semibold text-foreground-900">{scenario.pip}</p>
                </div>
                <div>
                  <p className="text-[11px] text-foreground-500">แนวโน้ม</p>
                  <p className={`flex items-center gap-1 text-sm font-semibold ${toneTextClass(scenario.tone)}`}>
                    <i
                      className={
                        scenario.tone === 'down'
                          ? 'ri-arrow-down-line'
                          : scenario.tone === 'flat'
                            ? 'ri-arrow-left-right-line'
                            : 'ri-arrow-up-line'
                      }
                    />
                    {scenario.trend}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-3 rounded-lg border border-background-200 bg-background-50 p-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground-900">
                <i className="ri-eye-line text-sm text-primary-600" />
                อ่านกราฟแบบนี้
              </p>
              <ul className="mt-2 space-y-1.5">
                {scenario.points.map((point, index) => (
                  <li key={point} className="flex gap-2 text-[11px] leading-relaxed text-foreground-600">
                    <span className="mt-[1px] flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-background-100 font-num text-[10px] font-semibold text-foreground-700">
                      {index + 1}
                    </span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-background-200 pt-3">
              <span className="flex min-w-0 items-center gap-1.5 text-xs leading-tight text-foreground-500">
                <i className="ri-calendar-line text-sm text-primary-600" />
                <span className="truncate">ตัวอย่างข้อมูล ณ 2 ต.ค. 2026</span>
              </span>
              <span className="shrink-0 rounded-full bg-background-100 px-2.5 py-1 text-[11px] font-medium text-foreground-600">
                ไม่ใช่ราคาจริง
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}